import React from 'react'
import Widget from './widget'
import { StatsDateBucketInterval } from '@/generated/enums'
import { Box } from '@chakra-ui/react'
import {
  WIDGET_UNIT_HEIGHT_PX,
  STATS_GREEN,
  STATS_BLUE,
  STATS_PURPLE,
  STATS_VIOLET,
  STATS_YELLOW,
  STATS_CYAN,
} from './dashboard_utils'
import TimeSeriesBarChart from '../time_series_bar_chart'
import TimeSeriesLineChart from '../time_series_line_chart'
import { TimeSeriesChartData } from '../time_series_utils'
import { ClickTrackingEventTypes } from '@/generated/enums'

export type GroupedTimeSeriesWidgetProps<K extends string> = {
  title: string
  helperText: string
  chartData: TimeSeriesChartData<K> | null
  legendKeys: K[]
  bucketInterval: StatsDateBucketInterval
  chartType: 'bar' | 'line'
  loading?: boolean
  error?: any
  colSpan?: number
  onClick?: () => void
  clickTrackingEventName?: ClickTrackingEventTypes
}

// these may need to be adjusted with widget sizes
const CHART_HEIGHT_PX = 2 * WIDGET_UNIT_HEIGHT_PX - 46
const MAX_Y_AXIS_TICKS = 6

const DEFAULT_TIME_SERIES_COLSPAN = 15

const BAR_GRAPH_WIDGET_COLORS = [
  STATS_CYAN,
  STATS_BLUE,
  STATS_GREEN,
  STATS_VIOLET,
  STATS_YELLOW,
  STATS_PURPLE,
]

const allowedYAxisTickIntervals = function* () {
  let exp = 0
  while (true) {
    yield 1 * 10 ** exp
    yield 2 * 10 ** exp
    yield 5 * 10 ** exp
    exp++
  }
}

const computeYAxisTickValues = <K extends string>(
  data: TimeSeriesChartData<K>,
  stacked = false,
): number[] => {
  const maxValue = Math.max(
    ...data.map((item) =>
      (Object.values(item.values) as number[]).reduce(
        (a, b) => (stacked ? a + b : Math.max(a, b)),
        0,
      ),
    ),
  )
  const allowedTickIntervalsGenerator = allowedYAxisTickIntervals()
  let tickInterval = 1
  while (!isNaN(tickInterval)) {
    tickInterval = allowedTickIntervalsGenerator.next().value
    if (maxValue / tickInterval <= MAX_Y_AXIS_TICKS) {
      break
    }
  }

  const tickValues = []
  for (let i = 0; i <= maxValue + tickInterval / 2; i += tickInterval) {
    tickValues.push(i)
  }
  return tickValues
}

const GroupedTimeSeriesWidget = <K extends string>({
  title,
  helperText,
  chartData,
  legendKeys,
  bucketInterval,
  chartType,
  loading,
  error,
  colSpan = DEFAULT_TIME_SERIES_COLSPAN,
  onClick,
}: GroupedTimeSeriesWidgetProps<K>) => {
  const [componentLoading, setComponentLoading] = React.useState<boolean>(false)

  let chart
  if (chartType === 'bar') {
    chart = (
      <TimeSeriesBarChart
        barColors={BAR_GRAPH_WIDGET_COLORS}
        bucketInterval={bucketInterval}
        chartData={chartData}
        legendKeys={legendKeys}
        legendPosition="top-right"
        margin={{ top: 20, right: 130, bottom: 25, left: 30 }}
        setLoading={setComponentLoading}
        yAxisLabels={computeYAxisTickValues(chartData, true)}
      />
    )
  } else if (chartType === 'line') {
    chart = (
      <TimeSeriesLineChart
        bucketInterval={bucketInterval}
        chartData={chartData}
        legendKeys={legendKeys}
        legendPosition="top-right"
        lineColors={BAR_GRAPH_WIDGET_COLORS}
        margin={{ top: 30, right: 130, bottom: 25, left: 30 }}
        setLoading={setComponentLoading}
        yAxisLabels={computeYAxisTickValues(chartData, false)}
      />
    )
  }
  return (
    <Widget
      colSpan={colSpan}
      emptyData={chartData && chartData.length === 0}
      error={error}
      helpText={helperText}
      label={title}
      loading={loading || componentLoading}
      onClick={onClick}
      rowSpan={2}
    >
      <Box h={CHART_HEIGHT_PX} w="100%">
        {chart}
      </Box>
    </Widget>
  )
}

export default GroupedTimeSeriesWidget
